/**
 * The runtime check that every admin verb declares a grant and a tenant source
 * (BUG-2000) — the half of `AdminVerbSpec`'s required fields `tsc` cannot hold.
 * `defineAdminRoute` runs it at module load; the access-table walk runs it over the whole registry.
 */
import { isKnownGrant } from '../../isomorphic/auth/grants';

/**
 * Every kind a `TenantSource` may name — the closed union as a runtime value, so
 * {@link assertVerbDeclared} can refuse a declaration that reached the registry
 * past `tsc` (a cast, an untyped caller). `define-admin-route.type-test.ts` holds
 * it equal to `TenantSource['from']` in both directions.
 */
export const TENANT_SOURCE_KINDS = [
  'record',
  'records',
  'input',
  'session',
  'scoped',
  'polymorphic',
  'shared_membership',
  'none',
] as const;

/**
 * Refuse a verb that does not declare BOTH a grant and a tenant source.
 *
 * `tsc` refuses an omission written plainly, but not one that reaches the
 * registry through a cast or an entry built without `defineAdminRoute`. Such a
 * verb fails no type check, and at request time it is either a 403 for every
 * role but super admin (no grant) or a 500 (no tenant) — wrong, and silent until
 * someone calls it. Running this at registry load makes the required `test` job
 * fail instead. The reasoned opt-outs (`{ none }`, `{ superAdmin }`,
 * `tenant: { from: 'none' }`) are the exemption list: they are declarations.
 *
 * @param opts - The route's path, the verb, and its spec as found on the entry.
 * @throws when the grant is missing or not one of the four declared shapes, or
 *   the tenant is missing or names a kind outside {@link TENANT_SOURCE_KINDS}.
 */
export function assertVerbDeclared(opts: { path: string; verb: string; spec: unknown }): void {
  const { path, verb } = opts;
  const spec: { grant?: unknown; tenant?: unknown } =
    typeof opts.spec === 'object' && opts.spec !== null ? opts.spec : {};
  if (!isGrantDeclaration(spec.grant)) {
    throw new Error(
      `[api-registry] ${verb} ${path}: declares no grant (found ${JSON.stringify(spec.grant) ?? 'undefined'}). ` +
        `Every admin verb names a grant from grants.ts, an { anyOf }, a { superAdmin: '<why>' } ` +
        `or a { none: '<kind> — <why>' } opt-out (ADR 062 — Access Grants).`,
    );
  }
  const from: unknown =
    typeof spec.tenant === 'object' && spec.tenant !== null ? Reflect.get(spec.tenant, 'from') : undefined;
  if (!TENANT_SOURCE_KINDS.some((kind) => kind === from)) {
    throw new Error(
      `[api-registry] ${verb} ${path}: declares no tenant source (found ${JSON.stringify(spec.tenant) ?? 'undefined'}). ` +
        `Every admin verb names one of: ${TENANT_SOURCE_KINDS.join(', ')} — ` +
        `a tenant-less verb says { from: 'none', reason } (ADR 062 — Access Grants).`,
    );
  }
}

/** Whether `value` is one of the four `GrantDeclaration` shapes, with real grants in it. */
function isGrantDeclaration(value: unknown): boolean {
  if (typeof value === 'string') return isKnownGrant(value);
  if (typeof value !== 'object' || value === null) return false;
  if ('none' in value) return typeof value.none === 'string';
  if ('superAdmin' in value) return typeof value.superAdmin === 'string';
  if ('anyOf' in value) {
    return Array.isArray(value.anyOf) && value.anyOf.length >= 2 && value.anyOf.every(isKnownGrant);
  }
  return false;
}
